import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import './RollingIntentBar.css';

interface Intent {
  icon: string;
  text: string;
  query: string;
  filters?: any;
}

const RollingIntentBar: React.FC = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  
  const intents: Intent[] = [
    { 
      icon: "🏠", 
      text: "First Time Home Buyer", 
      query: "first time buyer homes under 400k",
      filters: { maxPrice: 400000 }
    },
    { 
      icon: "💎", 
      text: "Luxury Homes $1M+", 
      query: "luxury homes over 1 million",
      filters: { minPrice: 1000000 }
    },
    { 
      icon: "🏊", 
      text: "Homes with Pools", 
      query: "homes with swimming pools",
      filters: { hasPool: true }
    },
    { 
      icon: "🏫", 
      text: "Near Top Schools", 
      query: "homes near good schools" 
    },
    { 
      icon: "🌵", 
      text: "Desert Views", 
      query: "desert view properties",
      filters: { viewType: 'desert' }
    },
    { 
      icon: "⚡", 
      text: "New Construction", 
      query: "new construction homes",
      filters: { isNewConstruction: true }
    },
    { 
      icon: "🏌️", 
      text: "Golf Communities", 
      query: "golf course homes",
      filters: { nearGolf: true }
    },
    { 
      icon: "📍", 
      text: "Downtown Condos", 
      query: "downtown phoenix condos",
      filters: { propertyType: 'condo', downtown: true }
    },
    {
      icon: "🔥",
      text: "Hot Deals",
      query: "price reduced homes",
      filters: { priceReduced: true }
    },
    {
      icon: "🏞️",
      text: "Mountain Views",
      query: "mountain view homes",
      filters: { viewType: 'mountain' }
    }
  ];

  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % intents.length);
      }, 3500); // Rotate every 3.5 seconds
      return () => clearInterval(interval);
    }
  }, [isPaused, intents.length]);

  const handleIntentClick = (intent: Intent) => {
    // Store the intent for the search portal to pick up
    localStorage.setItem('chatRealEstateQuery', intent.query);
    
    // Navigate to search with pre-populated context
    navigate('/search', { 
      state: { 
        initialQuery: intent.query,
        intentType: intent.text,
        filters: intent.filters
      }
    });
  };

  const getVisibleIntents = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + intents.length) % intents.length;
      visible.push({ ...intents[index], position: i });
    }
    return visible;
  };

  if (!isVisible) return null;

  return (
    <div className="rolling-intent-bar">
      <div className="intent-container">
        <div className="intent-prompt">
          <FontAwesomeIcon icon={faSearch} className="me-2" />
          <span>Looking for something specific?</span>
        </div>
        
        <div 
          className="intent-carousel"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="intent-track">
            {getVisibleIntents().map((intent, index) => (
              <Button
                key={`${intent.text}-${index}`}
                variant="outline-primary"
                className={`intent-pill ${intent.position === 0 ? 'active' : ''}`}
                onClick={() => handleIntentClick(intent)}
                style={{
                  transform: `translateX(${intent.position * 110}%)`,
                  opacity: intent.position === 0 ? 1 : 0.6,
                  scale: intent.position === 0 ? 1 : 0.9
                }}
              >
                <span className="intent-icon">{intent.icon}</span>
                <span className="intent-text">{intent.text}</span>
              </Button>
            ))}
          </div>
        </div>

        <button
          className="close-btn"
          onClick={() => setIsVisible(false)}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Mobile view - simplified */}
      <div className="intent-mobile">
        <Button
          variant="primary"
          size="sm"
          className="mobile-intent-btn"
          onClick={() => handleIntentClick(intents[currentIndex])}
        >
          <span className="intent-icon me-2">{intents[currentIndex].icon}</span>
          {intents[currentIndex].text}
        </Button>
      </div>
    </div>
  );
};

export default RollingIntentBar;